import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'

export interface WatchlistMovie {
  movieId: string
  tmdbId: number | null
  addedAt: string | null
  notes: string | null
  position: number
  isExternal: boolean
  externalTitle: string | null
  externalYear: number | null
  externalPosterPath: string | null
}

export interface WatchlistEntry {
  id: string
  name: string
  description: string | null
  isDefault: boolean
  color: string | null
  movies: WatchlistMovie[]
}

/**
 * Subscribes to users/{uid}/library/watchlists. The desktop writes a
 * single document with watchlist ids as top-level fields and movies
 * nested under each as a `movies` map; we unfold that into an array of
 * watchlists with their movies as a sorted array.
 */
export function useWatchlists(): { watchlists: WatchlistEntry[]; loading: boolean } {
  const { user } = useAuth()
  const [watchlists, setWatchlists] = useState<WatchlistEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setWatchlists([])
      setLoading(false)
      return
    }
    const ref = doc(firestore, `users/${user.uid}/library/watchlists`)
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setWatchlists([])
          setLoading(false)
          return
        }
        const data = snap.data() as Record<string, any>
        const list: WatchlistEntry[] = []
        for (const [id, raw] of Object.entries(data)) {
          if (!raw || typeof raw !== 'object') continue
          if (raw._deleted) continue
          if (id.includes('.')) continue // ghost keys from a past bug

          const moviesRaw = raw.movies && typeof raw.movies === 'object' ? raw.movies : {}
          const movies: WatchlistMovie[] = []
          for (const [movieKey, m] of Object.entries<any>(moviesRaw)) {
            if (!m || typeof m !== 'object' || m._deleted) continue
            movies.push({
              movieId: m.movieId ?? movieKey,
              tmdbId: typeof m.tmdbId === 'number' ? m.tmdbId : null,
              addedAt: typeof m.addedAt === 'string' ? m.addedAt : null,
              notes: m.notes ?? null,
              position: typeof m.position === 'number' ? m.position : 0,
              isExternal: !!m.isExternal,
              externalTitle: m.externalData?.title ?? null,
              externalYear: m.externalData?.year ?? null,
              externalPosterPath: m.externalData?.posterPath ?? null,
            })
          }
          movies.sort((a, b) => a.position - b.position)

          list.push({
            id,
            name: raw.name ?? 'Untitled',
            description: raw.description ?? null,
            isDefault: !!raw.isDefault,
            color: raw.color ?? null,
            movies,
          })
        }
        list.sort((a, b) => {
          if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1
          return a.name.localeCompare(b.name)
        })
        setWatchlists(list)
        setLoading(false)
      },
      (err) => {
        console.error('[useWatchlists]', err)
        setLoading(false)
      }
    )
    return unsub
  }, [user])

  return { watchlists, loading }
}
